import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {AxiosError} from "axios";
import {makeAutoObservable} from "mobx";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {ICouponStore} from "data/stores/coupon/coupon.store";
import type {IAxiosApiError} from "data/types/api";
import {ModalType} from "data/enums";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";

export interface IHOCMainController extends ViewController {
	get isLoadingUser(): boolean;
}

@injectable()
export class HOCMainController implements IHOCMainController {
	private playoffModalKey = "cfl-pck-playoff-modal";

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.CouponStore) private _couponStore: ICouponStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore
	) {
		makeAutoObservable(this);
	}

	dispose(): void {
		return;
	}

	init(param: void): void {
		this.fetchUser();
	}

	private async fetchCoupon() {
		try {
			await this._couponStore.fetch();
		} catch (error) {
			this._modalsStore.showAxiosError(error as AxiosError<IAxiosApiError>);
		}
	}

	private async fetchCountry() {
		try {
			await this._userStore.fetchCountry();
		} catch (error) {
			this._modalsStore.showAxiosError(error as AxiosError<IAxiosApiError>);
		}
	}

	private async showPlayoffModal() {
		try {
			await this._roundsStore.fetchRounds();

			const isPlayoff = this._roundsStore.isPlayoff;

			const isShowedPlayoffModal = JSON.parse(
				localStorage.getItem(this.playoffModalKey) || "false"
			) as boolean;

			if (!isShowedPlayoffModal && isPlayoff) {
				this._modalsStore.showModal(ModalType.PLAYOFF);

				localStorage.setItem(this.playoffModalKey, "true");
			}
		} catch (e) {
			console.error(e);
		}
	}

	private fetchUser(): void {
		void this.showPlayoffModal();

		void this._userStore.fetchUser().finally(() => {
			if (this._userStore.user) {
				void this.fetchCoupon();
				void this.fetchCountry();

				void this._userStore.fetchUserProfile().finally(() => {
					if (this._userStore.profile && !this._userStore.profile.terms) {
						this._modalsStore.showTerms();
					}
				});
			}
		});
	}

	get isLoadingUser(): boolean {
		return this._userStore.isRequestingUser;
	}
}
